import { Clipboard } from '@angular/cdk/clipboard'
import { Component, computed, DestroyRef, effect, inject, Input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms'
import { catchError, EMPTY, finalize, map, Observable, of, tap } from 'rxjs'
import { environment } from '@environments/environment'
import { SelfSettingsService } from '../../services/self-settings.service'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { SelfPrintPersonaCardComponent } from './self-print-persona-card/self-print-persona-card.component'
import { selfPrintingPersonaSettings, SelfPrintPersona } from '../../types/settings.type'
import { selfPrintingPersonaSettingsType } from '../../enums/settings.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'

type SavedPersonaSelfPrint = Readonly<{ isSelfPrint: boolean; limit: number | null }>

@Component({
  selector: 'add-self-services',
  standalone: true,
  imports: [SelfPrintPersonaCardComponent, NgxSkeletonLoaderModule, ButtonSpinnerDirective],
  providers: [SelfSettingsService],
  templateUrl: './self-services.component.html',
  styleUrl: './self-services.component.scss'
})
export class SelfServicesComponent {
  @Input() eventId: number
  @Input() eventSlug: string
  @Input() organizationSlug: string

  public readonly selfPrintLinkStatus = signal<string>('')
  public readonly personas = signal<SelfPrintPersona[]>([])
  public readonly personaSettingsLoading = signal(false)
  public readonly isSaving = signal(false)
  public readonly hasInvalidLimits = computed(() => this.personas().some((p) => p.enabled && p.limitControl.invalid))
  public readonly hasPendingChanges = computed(() => {
    this._personaEditsTick()
    if (this.personaSettingsLoading() || this.personas().length === 0) return false
    const saved = this._savedPersonaSnapshot()
    for (const persona of this.personas()) {
      if (!this._personaMatchesSnapshot(persona, saved.get(persona.id))) return true
    }
    return false
  })
  public readonly hasUnsavedChanges = this.hasPendingChanges

  private readonly _personaEditsTick = signal(0)
  private readonly _savedPersonaSnapshot = signal<ReadonlyMap<number, SavedPersonaSelfPrint>>(new Map())

  private readonly _clipboard = inject(Clipboard)
  private readonly _selfSettingsService = inject(SelfSettingsService)
  private readonly _authService = inject(AuthService)
  private readonly _alertService = inject(AlertService)
  private readonly _destroyRef = inject(DestroyRef)

  constructor() {
    effect((onCleanup) => {
      const workspaceId = this._authService.currentWorkspace?.id
      if (workspaceId == null || !this.eventId || !this.organizationSlug?.trim() || !this.eventSlug?.trim()) return

      this.personaSettingsLoading.set(true)
      const sub = this._selfSettingsService
        .getSelfSettings(workspaceId, this.eventId)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (res) => {
            this.personas.set(this.mapApiToPersonas(res.data))
            this._savedPersonaSnapshot.set(this._snapshotFromApiPersonasCards(res.data))
            this._personaEditsTick.update((n) => n + 1)
            this.personaSettingsLoading.set(false)
          },
          error: () => {
            this.personas.set([])
            this._savedPersonaSnapshot.set(new Map())
            this.personaSettingsLoading.set(false)
          }
        })
      onCleanup(() => sub.unsubscribe())
    })
  }

  /**
   * Get the self print link
   * @returns {string} - The self print link
   */
  public get selfPrintLink(): string {
    return `${environment.publicFormsUrl}self-print/${this.organizationSlug}/${this.eventSlug}`
  }

  /**
   * Copy the self print link and open it in a new tab
   */
  public copyAndOpenSelfPrintLink(): void {
    const link = this.selfPrintLink

    this.selfPrintLinkStatus.set('copied!')
    this._clipboard.copy(link)

    setTimeout(() => {
      this.selfPrintLinkStatus.set('')
      window.open(link, '_blank')
    }, 1000)
  }

  /**
   * Handles the self print toggle for a persona
   * @param payload {id: number; enabled: boolean} - The payload containing the persona ID and enabled status
   */
  public onPersonaSelfPrintToggled(payload: { id: number; enabled: boolean }): void {
    this.personas.update((list) =>
      list.map((persona) => {
        if (persona.id !== payload.id) return persona
        if (payload.enabled) persona.limitControl.enable({ emitEvent: false })
        else persona.limitControl.disable({ emitEvent: false })
        return { ...persona, enabled: payload.enabled }
      })
    )
    this._personaEditsTick.update((n) => n + 1)
  }

  /**
   * Handles the limit edited for a persona
   */
  public onPersonaLimitEdited(): void {
    this._personaEditsTick.update((n) => n + 1)
  }

  /**
   * Persists persona self-print settings (same idea as zones save bar).
   */
  public savePersonaSettings(): void {
    this.saveUnsavedChanges().pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Wizard contract: triggered from the unsaved-changes modal. Persists pending persona edits and
   * resolves once the call settles so the wizard can close right after.
   */
  public saveUnsavedChanges(): Observable<void> {
    if (this.isSaving()) return of(void 0)
    const workspaceId = this._authService.currentWorkspace?.id
    if (workspaceId == null || !this.eventId || !this.hasPendingChanges()) return of(void 0)
    if (this.hasInvalidLimits()) {
      this._alertService.alert(Alert.ERROR, 'Fix invalid print limits before saving.')
      return of(void 0)
    }

    const payload = this._buildUpdatePayload()
    if (payload.settings.length === 0) return of(void 0)

    this.isSaving.set(true)
    return this._selfSettingsService.updateSelfSettings(workspaceId, this.eventId, payload).pipe(
      tap((res) => {
        this.personas.set(this.mapApiToPersonas(res.data))
        this._savedPersonaSnapshot.set(this._snapshotFromApiPersonasCards(res.data))
        this._personaEditsTick.update((n) => n + 1)
        this._alertService.alert(Alert.SUCCESS, res.message)
      }),
      map(() => void 0),
      catchError((err) => {
        this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to save settings')
        return EMPTY
      }),
      finalize(() => this.isSaving.set(false))
    )
  }

  /**
   * Wizard contract: discard local edits when the user picks "Discard changes". Resets every
   * persona's enabled flag and limit back to the last saved snapshot.
   */
  public revertToSaved(): void {
    const snapshot = this._savedPersonaSnapshot()
    this.personas.update((personas) =>
      personas.map((persona) => {
        const saved = snapshot.get(persona.id)
        if (!saved) return persona
        persona.limitControl.setValue(saved.limit, { emitEvent: false })
        if (saved.isSelfPrint) persona.limitControl.enable({ emitEvent: false })
        else persona.limitControl.disable({ emitEvent: false })
        return { ...persona, enabled: saved.isSelfPrint }
      })
    )
    this._personaEditsTick.update((n) => n + 1)
  }

  /**
   * Validates the self print limit
   * @param control {AbstractControl} - The control to validate
   * @returns {ValidationErrors | null} - The validation errors or null if the limit is valid
   */
  private static selfPrintLimitValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value == null || control.value === '') return null
    if (typeof control.value !== 'number' || Number.isNaN(control.value)) return { limit: true }
    if (!Number.isInteger(control.value) || control.value < 0 || !Number.isSafeInteger(control.value)) return { limit: true }
    return null
  }

  /**
   * Maps API persona settings into wizard models with a reactive limit control.
   * @param settings {selfPrintingPersonaSettings} - The API persona settings
   * @returns {SelfPrintPersona[]} - The mapped persona settings
   */
  private mapApiToPersonas(settings: selfPrintingPersonaSettings): SelfPrintPersona[] {
    return settings.map((persona) => {
      const limit = persona.self_print_limit as number | null | undefined
      const initial: number | null = limit ?? null
      const isSelfPrint = persona.is_self_print ?? false
      const limitControl = new FormControl<number | null>({ value: initial, disabled: !isSelfPrint }, { validators: [SelfServicesComponent.selfPrintLimitValidator] })
      return {
        id: persona.id as number,
        name: (persona.name ?? '') as string,
        enabled: isSelfPrint,
        limitControl
      }
    })
  }

  /**
   * Builds the update payload for the self printing persona settings
   * @returns {settings: selfPrintingPersonaSettings} - The update payload
   */
  private _buildUpdatePayload(): { settings: selfPrintingPersonaSettings } {
    const saved = this._savedPersonaSnapshot()
    const settings: selfPrintingPersonaSettings = []

    for (const persona of this.personas()) {
      if (this._personaMatchesSnapshot(persona, saved.get(persona.id))) continue
      const personaCard: selfPrintingPersonaSettings[number] = {
        id: persona.id,
        name: persona.name,
        is_self_print: persona.enabled,
        type: selfPrintingPersonaSettingsType.SELFPRINT
      }
      if (persona.enabled) {
        personaCard.self_print_limit = this._emptyLimitToNull(persona.limitControl.value)
      }
      settings.push(personaCard)
    }

    return { settings }
  }

  /**
   * Handles the empty limit to null conversion
   * @param value {number | null | undefined} - The limit value
   * @returns {number | null} - The normalized limit value
   */
  private _emptyLimitToNull(value: number | null | undefined): number | null {
    return value ?? null
  }

  /** One pass over GET rows → map for O(1) lookups (no sort, no duplicate array shapes). */
  private _snapshotFromApiPersonasCards(personasCards: selfPrintingPersonaSettings | undefined): ReadonlyMap<number, SavedPersonaSelfPrint> {
    const map = new Map<number, SavedPersonaSelfPrint>()
    for (const persona of personasCards) {
      const id = persona.id as number
      const isSelfPrint = persona.is_self_print ?? false
      map.set(id, {
        isSelfPrint,
        limit: isSelfPrint ? this._emptyLimitToNull(persona.self_print_limit) : null
      })
    }
    return map
  }

  /**
   * Checks if a persona matches the snapshot
   * @param persona {SelfPrintPersona} - The persona
   * @param snap {SavedPersonaSelfPrint | undefined} - The snapshot
   * @returns {boolean} - True if the persona matches the snapshot
   */
  private _personaMatchesSnapshot(persona: SelfPrintPersona, snapshot: SavedPersonaSelfPrint | undefined): boolean {
    if (!snapshot) return false
    if (snapshot.isSelfPrint !== persona.enabled) return false
    if (!persona.enabled) return true
    if (persona.limitControl.invalid) return false
    return this._emptyLimitToNull(persona.limitControl.value) === snapshot.limit
  }
}
